// Detail-page deep-content sections: Critique, Novelty Decomposition, Paper Q&A history.
// Loaded after primitives.jsx + i18n.jsx but before screens/03-detail.jsx.

// =============================================================================
// 1) CRITIQUE — Reviewer-style strengths / weaknesses / open questions / repro risk
// =============================================================================

const CRITIQUE_DATA = {
  // Reviewer simulation scores (NeurIPS-style 1-5)
  reviewer: {
    soundness:    { value: 4, of: 5, note_ko: '주장과 실험이 잘 일치, 일부 ablation 누락', note_en: 'Claims well supported; some ablations missing' },
    contribution: { value: 3, of: 5, note_ko: 'DiT prior는 새롭지만 cache는 엔지니어링', note_en: 'DiT prior is novel; cache is engineering' },
    presentation: { value: 4, of: 5, note_ko: '명료, §4.3 표기법은 혼란 여지', note_en: 'Clear writing; §4.3 notation is potentially confusing' },
    confidence:   { value: 4, of: 5, note_ko: '리뷰어가 영역에 정통한 경우', note_en: 'Reviewer familiar with the field' },
    overallEst:   { ko: '6 / 7-7-6 평균 (Borderline accept → Accept)', en: '6 / 7-7-6 mean (Borderline accept → Accept)' },
  },
  strengths: [
    {
      title_ko: 'D-NeRF 새로운 SoTA',
      title_en: 'New SoTA on D-NeRF',
      body_ko: '31.74 PSNR · +1.32 over Deformable-3DGS, 8 views 조건에서 통계적으로 의미있는 격차.',
      body_en: '31.74 PSNR, +1.32 over Deformable-3DGS at 8 views; statistically meaningful gap.',
      page: '§5.1, Tbl. 1, p. 14',
    },
    {
      title_ko: '얇은 형상 flicker 완화 시각적으로 명확',
      title_en: 'Thin-geometry flicker visibly resolved',
      body_ko: 'Fig. 5 supplementary video에서 케이블/와이어 영역의 flicker 거의 사라짐 — 본 프로젝트의 알려진 실패 모드.',
      body_en: 'Fig. 5 supplementary video: cable/wire flicker nearly eliminated — our known failure mode.',
      page: 'Fig. 5, p. 16',
    },
    {
      title_ko: '단일 4090에서 학습 가능 (11시간)',
      title_en: 'Single-GPU training (11h on a 4090)',
      body_ko: '경쟁 라인 (e.g. K-Planes 4 × A100)과 달리 학계 재현 가능.',
      body_en: 'Reproducible in an academic lab; competing lines need 4 × A100.',
      page: '§4.2, p. 13',
    },
    {
      title_ko: 'Canonical-id cache ablation 정성적',
      title_en: 'Canonical-id cache ablation is honest',
      body_ko: 'Cache만 추가했을 때 +0.1 PSNR / 3.1× speed로 명시 — speed 기여를 과대주장하지 않음.',
      body_en: 'Reports cache-only as +0.1 PSNR / 3.1× speed — does not overclaim cache as quality contribution.',
      page: 'Tbl. 4, p. 17',
    },
  ],
  weaknesses: [
    {
      title_ko: '"Open" 주장이 약함 — 사전학습 코퍼스 비공개',
      title_en: '"Open" framing is weak — pretrain corpus is closed',
      body_ko: '28k 클립 비공개. INT4 체크포인트만 공개되어, 새 도메인에서 prior를 재학습할 수 없음. 1티어 reviewer가 가장 먼저 짚을 부분.',
      body_en: 'The 28k-clip corpus is not released. Only an INT4 checkpoint is — so the prior cannot be re-trained on new domains. Tier-1 reviewers will catch this first.',
      page: 'Limitations, §6, p. 19',
      severity: 'high',
    },
    {
      title_ko: 'D-NeRF만 평가, 어려운 dataset 누락',
      title_en: 'Evaluates only on D-NeRF; harder datasets missing',
      body_ko: 'HyperNeRF, NeRF-DS 모두 누락. NeRF-DS의 specular 표면이 DiT prior를 깨뜨릴 가능성 있음.',
      body_en: 'No HyperNeRF, no NeRF-DS. NeRF-DS specular surfaces likely break the DiT prior.',
      page: '§5, missing tables',
      severity: 'high',
    },
    {
      title_ko: '4-bit cache의 loss bound 미증명',
      title_en: 'No bound on the 4-bit cache reconstruction error',
      body_ko: 'Fig. 4의 정성적 비교 외에는 증거 없음. K sweep도 PSNR delta만 — flicker 같은 metric은 빠짐.',
      body_en: 'Only qualitative Fig. 4. K-sweep reports PSNR delta but no temporal metric (flicker, LPIPS-t).',
      page: '§3.3, p. 10',
      severity: 'med',
    },
    {
      title_ko: 'Sparse-view (≤4) 성능 급격히 하락',
      title_en: 'Sparse-view (≤4) performance collapses',
      body_ko: '§5.2 ablation: 8→4 view에서 -3.2 PSNR. 본 프로젝트는 4-cam — 직접 적용 어려움.',
      body_en: 'Per §5.2 ablation, 8→4 views drops by 3.2 PSNR. Our rig has 4 cams — direct adoption is hard.',
      page: 'Tbl. 6, p. 17',
      severity: 'med',
    },
  ],
  open: [
    {
      title_ko: '다른 도메인에서 prior의 transfer는?',
      title_en: 'Does the prior transfer across domains?',
      body_ko: '자연 씬 코퍼스 → 금속·industrial 씬으로 transfer 실험 없음. 1티어 follow-up paper의 핵심 갭.',
      body_en: 'No transfer experiment from natural-scene corpus to metallic/industrial scenes. This is the core gap for a follow-up tier-1 paper.',
    },
    {
      title_ko: '논문 prior가 light/material을 baking하는가?',
      title_en: 'Does the prior bake in lighting / material?',
      body_ko: '만약 그렇다면, lighting variation이 큰 우리 데이터에서 무너질 가능성. §3.1 분석 부재.',
      body_en: 'If yes, our high-variance lighting will break it. §3.1 has no analysis.',
    },
    {
      title_ko: 'Cache 메모리 vs 코드북 크기의 Pareto?',
      title_en: 'What is the Pareto front for cache memory vs codebook size?',
      body_ko: 'K=64 선택 근거는 PSNR뿐; 메모리·디스크 footprint는 부록에만 일부.',
      body_en: 'K=64 is justified by PSNR; memory + disk footprint only partially in appendix.',
    },
  ],
  repro: [
    {
      title_ko: '학습 재현',
      title_en: 'Reproducing training',
      body_ko: '비공개 코퍼스 때문에 prior 학습은 불가능. Public Open-X subset (8k clips)로 부분 재학습 시 -0.7 PSNR 예상.',
      body_en: 'Pretraining is infeasible due to closed corpus. Partial retrain on a public Open-X subset (8k clips) likely loses 0.7 PSNR.',
      risk: 'high',
    },
    {
      title_ko: '추론 재현',
      title_en: 'Reproducing inference',
      body_ko: 'MIT 라이선스 PyTorch 저장소, INT4 weights 제공 — 가능.',
      body_en: 'MIT-licensed PyTorch repo with INT4 weights — fine.',
      risk: 'low',
    },
    {
      title_ko: '수치 재현',
      title_en: 'Reproducing the numbers',
      body_ko: 'D-NeRF v2 split 사용 — 일부 논문이 v1을 쓰므로 직접 비교 불가. Bench Memory에서 정규화 필요.',
      body_en: 'Uses D-NeRF v2 split; some papers report on v1, so direct comparison breaks. Normalize via Bench Memory.',
      risk: 'med',
    },
  ],
};

function CritiqueSection() {
  const { t, lang } = useT();
  const ko = lang === 'ko';
  const r = CRITIQUE_DATA.reviewer;

  return (
    <section className="pp-section pp-critique">
      <SectionHead
        kicker="E"
        title={ko ? '리뷰어 시뮬레이션 · 장단점' : 'Reviewer simulation · Strengths & Weaknesses'}
        sub={ko ? 'NeurIPS·CVPR 리뷰어 관점에서의 구조화 비평. 출처는 페이지 인용.' : 'Structured critique from a NeurIPS/CVPR-reviewer lens. All claims cite a page.'}
        action={
          <div className="row gap-2">
            <span className="chip violet" style={{ height: 22 }}><I name="sparkles" size={10}/> Haiku 4.5 · {ko ? '검증된 출처' : 'grounded'}</span>
          </div>
        }
      />

      {/* Reviewer scorecard */}
      <div className="pp-critique-scorecard">
        <div className="pp-critique-scorecard-head">
          <div className="row gap-2" style={{ alignItems: 'baseline' }}>
            <I name="quote" size={13} style={{ color: 'var(--acc-violet)' }}/>
            <span style={{ fontSize: 12, color: 'var(--fg-0)', fontWeight: 500 }}>
              {ko ? '예상 리뷰어 점수 · 1티어 학회 기준' : 'Predicted reviewer scores · tier-1 venue scale'}
            </span>
          </div>
          <span className="mono" style={{ fontSize: 11, color: 'var(--acc-violet)' }}>
            {ko ? '평균 6 · Accept' : 'Avg 6 · Accept'}
          </span>
        </div>
        <div className="pp-critique-scores">
          <ReviewerScore label={ko ? 'Soundness' : 'Soundness'} {...r.soundness} note={ko ? r.soundness.note_ko : r.soundness.note_en}/>
          <ReviewerScore label={ko ? 'Contribution' : 'Contribution'} {...r.contribution} note={ko ? r.contribution.note_ko : r.contribution.note_en}/>
          <ReviewerScore label={ko ? 'Presentation' : 'Presentation'} {...r.presentation} note={ko ? r.presentation.note_ko : r.presentation.note_en}/>
          <ReviewerScore label={ko ? 'Confidence' : 'Confidence'} {...r.confidence} note={ko ? r.confidence.note_ko : r.confidence.note_en}/>
        </div>
      </div>

      {/* Strengths + Weaknesses pair */}
      <div className="pp-critique-grid">
        <CritiqueColumn
          tone="ok"
          icon="check"
          title={ko ? '강점' : 'Strengths'}
          subtitle={ko ? '저자가 잘 한 것' : 'What the paper does well'}
          items={CRITIQUE_DATA.strengths.map(s => ({
            title: ko ? s.title_ko : s.title_en,
            body:  ko ? s.body_ko  : s.body_en,
            page: s.page,
          }))}
        />
        <CritiqueColumn
          tone="danger"
          icon="minus"
          title={ko ? '약점' : 'Weaknesses'}
          subtitle={ko ? '리뷰어가 짚을 부분' : 'What a reviewer would push back on'}
          items={CRITIQUE_DATA.weaknesses.map(w => ({
            title: ko ? w.title_ko : w.title_en,
            body:  ko ? w.body_ko  : w.body_en,
            page: w.page,
            severity: w.severity,
          }))}
        />
      </div>

      {/* Open questions + Repro risk pair */}
      <div className="pp-critique-grid">
        <CritiqueColumn
          tone="cyan"
          icon="question"
          title={ko ? '열린 질문' : 'Open questions'}
          subtitle={ko ? '논문이 명시적으로 답하지 않은 것' : 'What the paper does not answer'}
          items={CRITIQUE_DATA.open.map(o => ({
            title: ko ? o.title_ko : o.title_en,
            body:  ko ? o.body_ko  : o.body_en,
          }))}
        />
        <CritiqueColumn
          tone="warn"
          icon="shield"
          title={ko ? '재현성 리스크' : 'Reproducibility risk'}
          subtitle={ko ? '직접 돌려보려 할 때의 함정' : 'What breaks when you try to run it'}
          items={CRITIQUE_DATA.repro.map(r => ({
            title: ko ? r.title_ko : r.title_en,
            body:  ko ? r.body_ko  : r.body_en,
            severity: r.risk,
          }))}
        />
      </div>
    </section>
  );
}

function ReviewerScore({ label, value, of, note }) {
  return (
    <div className="pp-rev-score">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 10.5, letterSpacing: '0.10em', textTransform: 'uppercase', color: 'var(--fg-3)' }}>{label}</span>
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>{value}/{of}</span>
      </div>
      <div className="pp-rev-score-pips">
        {Array.from({ length: of }).map((_, i) => (
          <span key={i} className={'pp-rev-pip' + (i < value ? ' is-on' : '')}/>
        ))}
      </div>
      <div style={{ fontSize: 11, color: 'var(--fg-2)', lineHeight: 1.45, marginTop: 6 }}>{note}</div>
    </div>
  );
}

function CritiqueColumn({ tone, icon, title, subtitle, items }) {
  const toneMap = {
    ok: 'var(--ok)', danger: 'var(--danger)', cyan: 'var(--acc-cyan)', warn: 'var(--warn)',
  };
  const c = toneMap[tone];
  return (
    <div className={'pp-critique-col is-' + tone}>
      <div className="pp-critique-col-head" style={{ borderColor: `${c}55`, color: c }}>
        <div className="pp-critique-col-icon" style={{ background: `${c}1A`, color: c, borderColor: `${c}40` }}>
          <I name={icon} size={13}/>
        </div>
        <div className="col gap-1" style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, color: 'var(--fg-0)', fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{subtitle}</div>
        </div>
        <span className="mono" style={{ fontSize: 11, color: c }}>{String(items.length).padStart(2, '0')}</span>
      </div>
      <div className="pp-critique-col-body">
        {items.map((it, i) => <CritiqueItem key={i} num={i + 1} item={it} accent={c}/>)}
      </div>
    </div>
  );
}

function CritiqueItem({ num, item, accent }) {
  const sevMap = { high: 'var(--danger)', med: 'var(--warn)', low: 'var(--ok)' };
  return (
    <div className="pp-critique-item">
      <div className="pp-critique-item-num mono" style={{ color: accent }}>{String(num).padStart(2, '0')}</div>
      <div className="col gap-1" style={{ minWidth: 0, flex: 1 }}>
        <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: 'var(--fg-0)', fontWeight: 500 }}>{item.title}</span>
          {item.severity && (
            <span className="mono" style={{
              fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: sevMap[item.severity],
              padding: '1px 5px', borderRadius: 3,
              border: `1px solid ${sevMap[item.severity]}55`,
            }}>{item.severity}</span>
          )}
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--fg-1)', lineHeight: 1.55 }}>{item.body}</div>
        {item.page && (
          <div className="row gap-1" style={{ marginTop: 2 }}>
            <I name="quote" size={9} style={{ color: 'var(--fg-3)' }}/>
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>{item.page}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// 2) NOVELTY DECOMPOSITION — vs prior / vs concurrent / genuine contribution
// =============================================================================

const NOVELTY_DATA = {
  headline_ko: 'DiT prior를 변위 분포로 distill한 것이 진짜 새로움. Canonical-id cache는 알려진 VQ baking 패턴의 응용.',
  headline_en: "Distilling a DiT prior over displacement distribution is the genuinely new piece. The canonical-id cache is an applied case of known VQ baking.",
  // breakdown of the 0.81 novelty score
  decomposition: [
    { label_ko: '진짜 새로움',   label_en: 'Genuinely new',  value: 0.62, color: 'var(--acc-indigo)' },
    { label_ko: '재조합',         label_en: 'Recombination', value: 0.30, color: 'var(--acc-violet)' },
    { label_ko: '엔지니어링',     label_en: 'Engineering',   value: 0.08, color: 'var(--fg-3)' },
  ],
  vsPrior: [
    {
      paper: 'Deformable-3DGS',
      venue: 'CVPR 24',
      sameKey_ko: 'Deformation field 정식화',
      sameKey_en: 'Deformation field formulation',
      sameBody_ko: 'Canonical position + per-Gaussian displacement 𝒟(id, t) 구조 동일',
      sameBody_en: 'Canonical position + per-Gaussian displacement 𝒟(id, t) is identical',
      diffKey_ko: '𝒟의 parameterization',
      diffKey_en: 'How 𝒟 is parameterized',
      diffBody_ko: 'Park 24는 per-scene MLP, GS-DiT는 frozen distilled DiT + cache',
      diffBody_en: 'Park 24 uses a per-scene MLP; GS-DiT uses a frozen distilled DiT + cache',
    },
    {
      paper: 'DiT (Diffusion Transformers)',
      venue: 'ICCV 23',
      sameKey_ko: 'Diffusion Transformer 백본',
      sameKey_en: 'Diffusion Transformer backbone',
      sameBody_ko: 'Class-conditional patch DiT 구조 그대로 사용',
      sameBody_en: 'Same class-conditional patch-DiT architecture',
      diffKey_ko: '적용 도메인',
      diffKey_en: 'Application domain',
      diffBody_ko: 'DiT는 image, GS-DiT는 변위 분포에 distill — 이것이 핵심 contribution',
      diffBody_en: 'DiT models images; GS-DiT distills DiT over a displacement distribution — this is the core contribution',
    },
  ],
  vsConcurrent: [
    {
      paper: '4DGS-Cache',
      venue: 'SIGGRAPH 26',
      same_ko: 'Per-Gaussian 변위를 lookup 구조로 baking',
      same_en: 'Bakes per-Gaussian displacement into a lookup',
      diff_ko: 'Triplane vs codebook · 4DGS-Cache는 메모리 800MB+ 사용',
      diff_en: 'Triplane vs codebook; 4DGS-Cache spends 800MB+ memory',
      delta_ko: '실용성에서 GS-DiT 우위 (모바일·임베디드 가능)',
      delta_en: 'GS-DiT wins on deployability (mobile/embedded feasible)',
    },
    {
      paper: 'Sparse-View 3DGS Distillation',
      venue: 'ICCV 25',
      same_ko: 'Pretrained foundation model을 prior로 distill',
      same_en: 'Distills a pretrained foundation model as prior',
      diff_ko: 'Distillation 대상 — geometry depth vs 시간적 변위',
      diff_en: 'Distillation target — depth/normal vs temporal displacement',
      delta_ko: '같은 방법론 패밀리이지만 직교한 문제 해결',
      delta_en: 'Same methodology family; orthogonal problems',
    },
  ],
  // What's left for follow-up tier-1 work
  gaps: [
    { ko: 'Cross-domain prior transfer (자연 씬 → metallic·industrial) 미해명', en: 'Cross-domain prior transfer is unproven' },
    { ko: 'Sparse-view (≤4) + DiT prior 결합 미시도', en: 'Sparse-view (≤4) + DiT prior combo is untried' },
    { ko: '재학습 가능한 open pretrain corpus 부재', en: 'Open re-trainable pretrain corpus does not exist' },
  ],
};

function NoveltySection() {
  const { lang } = useT();
  const ko = lang === 'ko';
  return (
    <section className="pp-section pp-novelty">
      <SectionHead
        kicker="F"
        title={ko ? 'Novelty 분해 — 무엇이 진짜로 새로운가' : 'Novelty decomposition — what is actually new'}
        sub={ko ? '0.81 점수를 진짜 새로움 / 재조합 / 엔지니어링으로 분해. 1티어 reviewer가 가장 먼저 분해함.' : 'Decomposes the 0.81 score into genuinely-new / recombination / engineering. The first thing a tier-1 reviewer does.'}
        action={
          <button className="btn sm ghost"><I name="branch" size={11}/> {ko ? '계보 그래프' : 'Lineage graph'}</button>
        }
      />

      {/* Headline + composition bar */}
      <div className="pp-novelty-headline-card">
        <div className="pp-novelty-headline">
          <I name="sparkles" size={14} style={{ color: 'var(--acc-violet)' }}/>
          <span style={{ fontSize: 13.5, color: 'var(--fg-0)', lineHeight: 1.5 }}>
            {ko ? NOVELTY_DATA.headline_ko : NOVELTY_DATA.headline_en}
          </span>
        </div>
        <NoveltyComposition data={NOVELTY_DATA.decomposition} ko={ko}/>
      </div>

      {/* vs Prior */}
      <div className="pp-novelty-block">
        <div className="pp-novelty-block-head">
          <div className="pp-novelty-step">
            <I name="clock" size={11}/>
            <span>{ko ? '선행 연구 대비' : 'vs Prior work'}</span>
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>
            {ko ? '같은 것은 회색, 다른 것은 강조' : 'gray = same, highlighted = different'}
          </span>
        </div>
        <div className="pp-novelty-cards">
          {NOVELTY_DATA.vsPrior.map((p, i) => (
            <NoveltyVsCard key={i} ko={ko}
              paper={p.paper} venue={p.venue}
              same={{ k: ko ? p.sameKey_ko : p.sameKey_en, b: ko ? p.sameBody_ko : p.sameBody_en }}
              diff={{ k: ko ? p.diffKey_ko : p.diffKey_en, b: ko ? p.diffBody_ko : p.diffBody_en }}
            />
          ))}
        </div>
      </div>

      {/* vs Concurrent */}
      <div className="pp-novelty-block">
        <div className="pp-novelty-block-head">
          <div className="pp-novelty-step" style={{ background: 'rgba(106,215,240,0.08)', color: 'var(--acc-cyan)', borderColor: 'rgba(106,215,240,0.30)' }}>
            <I name="compare" size={11}/>
            <span>{ko ? '동시기 연구 대비' : 'vs Concurrent work'}</span>
          </div>
          <span style={{ fontSize: 11.5, color: 'var(--fg-2)' }}>
            {ko ? '같은 시기에 비슷한 문제를 푼 연구와의 delta' : 'how this differs from concurrent attacks on the same problem'}
          </span>
        </div>
        <div className="pp-novelty-cards">
          {NOVELTY_DATA.vsConcurrent.map((p, i) => (
            <NoveltyConcCard key={i} ko={ko} paper={p.paper} venue={p.venue}
              same={ko ? p.same_ko : p.same_en}
              diff={ko ? p.diff_ko : p.diff_en}
              delta={ko ? p.delta_ko : p.delta_en}
            />
          ))}
        </div>
      </div>

      {/* Genuine contribution + Gaps */}
      <div className="pp-novelty-row">
        <div className="pp-novelty-genuine">
          <div className="row gap-2" style={{ marginBottom: 10 }}>
            <I name="target" size={13} style={{ color: 'var(--acc-indigo)' }}/>
            <span style={{ fontSize: 12, color: 'var(--acc-indigo)', letterSpacing: '0.06em', textTransform: 'uppercase' }} className="mono">
              {ko ? '진짜 기여' : 'Genuine contribution'}
            </span>
          </div>
          <div className="pp-novelty-genuine-list">
            <GenuineRow num="1" label={ko ? 'DiT prior를 변위 분포에 distill' : 'Distill DiT prior over displacement'} weight={0.55}/>
            <GenuineRow num="2" label={ko ? '4-bit codebook으로 inference cache' : '4-bit codebook inference cache'} weight={0.25} subtle/>
            <GenuineRow num="3" label={ko ? '28k 클립 학습 레시피 (closed)' : '28k-clip training recipe (closed)'} weight={0.20} subtle/>
          </div>
        </div>
        <div className="pp-novelty-gaps">
          <div className="row gap-2" style={{ marginBottom: 10 }}>
            <I name="zap" size={13} style={{ color: 'var(--acc-violet)' }}/>
            <span style={{ fontSize: 12, color: 'var(--acc-violet)', letterSpacing: '0.06em', textTransform: 'uppercase' }} className="mono">
              {ko ? '남은 갭 — follow-up 후보' : 'Remaining gaps — follow-up candidates'}
            </span>
          </div>
          <div className="col gap-2">
            {NOVELTY_DATA.gaps.map((g, i) => (
              <div key={i} className="pp-novelty-gap-row">
                <span className="mono pp-novelty-gap-num">G{i + 1}</span>
                <span style={{ fontSize: 12, color: 'var(--fg-1)', lineHeight: 1.5 }}>{ko ? g.ko : g.en}</span>
              </div>
            ))}
          </div>
          <button className="btn sm primary" style={{ marginTop: 12, alignSelf: 'flex-start' }}>
            <I name="sparkles" size={11}/>
            {ko ? '이 갭으로 Idea Forge 열기' : 'Open Idea Forge from these gaps'}
          </button>
        </div>
      </div>
    </section>
  );
}

function NoveltyComposition({ data, ko }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="pp-novelty-comp">
      <div className="pp-novelty-comp-bar">
        {data.map((d, i) => (
          <div key={i} className="pp-novelty-comp-seg"
            style={{ width: `${(d.value / total) * 100}%`, background: d.color }}
            title={`${ko ? d.label_ko : d.label_en} · ${Math.round((d.value / total) * 100)}%`}
          />
        ))}
      </div>
      <div className="pp-novelty-comp-legend">
        {data.map((d, i) => (
          <div key={i} className="pp-novelty-comp-leg">
            <span className="pp-novelty-comp-swatch" style={{ background: d.color }}/>
            <span style={{ fontSize: 11, color: 'var(--fg-1)' }}>{ko ? d.label_ko : d.label_en}</span>
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)', marginLeft: 'auto' }}>{Math.round((d.value / total) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function NoveltyVsCard({ paper, venue, same, diff, ko }) {
  return (
    <div className="pp-novelty-vs-card">
      <div className="pp-novelty-vs-head">
        <div className="col gap-1" style={{ minWidth: 0 }}>
          <span className="serif" style={{ fontSize: 14, color: 'var(--fg-0)' }}>{paper}</span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{venue}</span>
        </div>
        <I name="diff" size={14} style={{ color: 'var(--fg-3)' }}/>
      </div>
      <div className="pp-novelty-vs-body">
        <div className="pp-novelty-row-line is-same">
          <span className="pp-novelty-line-tag">{ko ? '같음' : 'same'}</span>
          <div className="col gap-1">
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>{same.k}</span>
            <span style={{ fontSize: 11, color: 'var(--fg-3)', lineHeight: 1.45 }}>{same.b}</span>
          </div>
        </div>
        <div className="pp-novelty-row-line is-diff">
          <span className="pp-novelty-line-tag is-diff">{ko ? '다름' : 'diff'}</span>
          <div className="col gap-1">
            <span style={{ fontSize: 12, color: 'var(--fg-0)' }}>{diff.k}</span>
            <span style={{ fontSize: 11, color: 'var(--fg-1)', lineHeight: 1.45 }}>{diff.b}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoveltyConcCard({ paper, venue, same, diff, delta, ko }) {
  return (
    <div className="pp-novelty-conc-card">
      <div className="pp-novelty-vs-head">
        <div className="col gap-1" style={{ minWidth: 0 }}>
          <span className="serif" style={{ fontSize: 14, color: 'var(--fg-0)' }}>{paper}</span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{venue}</span>
        </div>
        <span className="chip cyan" style={{ height: 20 }}>{ko ? '동시기' : 'concurrent'}</span>
      </div>
      <div className="col gap-2" style={{ marginTop: 10 }}>
        <div className="row gap-2" style={{ alignItems: 'flex-start' }}>
          <span className="pp-novelty-conc-bullet" style={{ background: 'var(--fg-3)' }}/>
          <span style={{ fontSize: 11.5, color: 'var(--fg-2)', lineHeight: 1.5 }}>{same}</span>
        </div>
        <div className="row gap-2" style={{ alignItems: 'flex-start' }}>
          <span className="pp-novelty-conc-bullet" style={{ background: 'var(--acc-cyan)', boxShadow: '0 0 6px var(--acc-cyan)' }}/>
          <span style={{ fontSize: 12, color: 'var(--fg-0)', lineHeight: 1.5 }}>{diff}</span>
        </div>
      </div>
      <div className="pp-novelty-delta">
        <I name="zap" size={11} style={{ color: 'var(--acc-cyan)' }}/>
        <span style={{ fontSize: 11.5, color: 'var(--fg-1)' }}>{delta}</span>
      </div>
    </div>
  );
}

function GenuineRow({ num, label, weight, subtle }) {
  return (
    <div className="pp-genuine-row">
      <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)', width: 16 }}>{num}</span>
      <span style={{ fontSize: 12, color: subtle ? 'var(--fg-2)' : 'var(--fg-0)', flex: 1 }}>{label}</span>
      <div className="pp-genuine-bar">
        <div className="pp-genuine-bar-fill" style={{
          width: `${weight * 100}%`,
          background: subtle ? 'var(--fg-3)' : 'linear-gradient(90deg, var(--acc-indigo), var(--acc-violet))',
        }}/>
      </div>
      <span className="mono" style={{ fontSize: 10.5, color: subtle ? 'var(--fg-3)' : 'var(--acc-indigo)', width: 28, textAlign: 'right' }}>{Math.round(weight * 100)}%</span>
    </div>
  );
}

// =============================================================================
// 3) PAPER Q&A — past questions you've asked AI about THIS paper
// =============================================================================

function PaperQASection({ paperId }) {
  const { lang } = useT();
  const ko = lang === 'ko';
  const all = (typeof QUESTIONS !== 'undefined' ? QUESTIONS : []).filter(q => q.paperId === paperId);
  const [cat, setCat] = React.useState('all');
  const filtered = cat === 'all' ? all : all.filter(q => q.cat === cat);
  const nav = React.useContext(NavContext);

  const cats = (typeof QA_CATEGORIES !== 'undefined' ? QA_CATEGORIES : []);
  const visibleCats = cats.filter(c => c.id === 'all' || all.some(q => q.cat === c.id));

  return (
    <section className="pp-section pp-paperqa">
      <SectionHead
        kicker="G"
        title={ko ? '이 논문에 대한 내 질문' : 'Your questions on this paper'}
        sub={ko ? `${all.length}개 질문 · 시간 역순 · 카테고리로 필터` : `${all.length} questions · newest first · filterable by category`}
        action={
          <div className="row gap-2">
            <button className="btn sm ghost" onClick={() => nav.navigate && nav.navigate('questions')}>
              <I name="history" size={11}/> {ko ? '전체 Q&A 기록' : 'All Q&A history'}
            </button>
            <button className="btn sm primary">
              <I name="plus" size={11}/> {ko ? '새 질문' : 'New question'}
            </button>
          </div>
        }
      />

      {/* category filter */}
      <div className="pp-paperqa-cats">
        {visibleCats.map(c => {
          const n = c.id === 'all' ? all.length : all.filter(q => q.cat === c.id).length;
          return (
            <button key={c.id}
              className={'pp-paperqa-cat' + (cat === c.id ? ' is-on' : '')}
              onClick={() => setCat(c.id)}>
              <span>{ko ? c.ko : c.en}</span>
              <span className="mono pp-paperqa-cat-n">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="pp-paperqa-list">
        {filtered.length === 0 && (
          <div className="pp-paperqa-empty">
            {ko ? '이 카테고리에 질문이 없습니다.' : 'No questions in this category yet.'}
          </div>
        )}
        {filtered.map(q => <QARow key={q.id} q={q} ko={ko}/>)}
      </div>
    </section>
  );
}

function QARow({ q, ko, defaultOpen }) {
  const [open, setOpen] = React.useState(!!defaultOpen);
  const cats = (typeof QA_CATEGORIES !== 'undefined' ? QA_CATEGORIES : []);
  const cat = cats.find(c => c.id === q.cat) || { ko: q.cat, en: q.cat };
  return (
    <div className={'pp-qa-row' + (open ? ' is-open' : '')}>
      <button className="pp-qa-q" onClick={() => setOpen(o => !o)}>
        <div className="row gap-2" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span className="chip accent" style={{ height: 20 }}>{ko ? cat.ko : cat.en}</span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{ko ? q.when : q.whenEn}</span>
          {q.starred && <I name="star" size={11} style={{ color: 'var(--warn)' }}/>}
          {q.sources > 0 && (
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
              <I name="quote" size={9} style={{ display: 'inline-block', verticalAlign: -1 }}/> {q.sources}{ko ? '개 출처' : ' sources'}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13.5, color: 'var(--fg-0)', marginTop: 5, fontWeight: 500, textAlign: 'left', lineHeight: 1.45 }}>
          {ko ? q.q : q.qEn}
        </div>
        <I name="chevronDown" size={13} className={'pp-qa-chev' + (open ? ' is-open' : '')}/>
      </button>
      {open && (
        <div className="pp-qa-a">
          <div className="pp-qa-a-body">{ko ? q.a : q.aEn}</div>
          {q.follow && q.follow.length > 0 && (
            <div className="pp-qa-follow">
              <div className="section-title" style={{ marginBottom: 6 }}>{ko ? '후속 질문' : 'Follow-ups'}</div>
              {q.follow.map((f, i) => (
                <button key={i} className="pp-qa-follow-q">
                  <I name="chevronRight" size={10} style={{ color: 'var(--acc-indigo)' }}/>
                  <span>{f}</span>
                </button>
              ))}
            </div>
          )}
          <div className="pp-qa-a-actions">
            <button className="btn sm ghost"><I name="copy" size={11}/> {ko ? '복사' : 'Copy'}</button>
            <button className="btn sm ghost"><I name="book" size={11}/> {ko ? '위키로' : 'To wiki'}</button>
            <button className="btn sm ghost"><I name="pin" size={11}/> {ko ? '프로젝트에 고정' : 'Pin to project'}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

if (typeof document !== 'undefined' && !document.getElementById('pp-detail-sections-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-detail-sections-styles';
  s.textContent = `
  /* ============ CRITIQUE ============ */
  .pp-critique { display: flex; flex-direction: column; gap: 14px; }
  .pp-critique-scorecard {
    background: linear-gradient(180deg, rgba(180,155,255,0.05), transparent 70%);
    border: 1px solid rgba(180,155,255,0.22);
    border-radius: 12px;
    padding: 14px 16px;
    margin-top: 4px;
  }
  .pp-critique-scorecard-head {
    display: flex; justify-content: space-between; align-items: baseline;
    padding-bottom: 10px; margin-bottom: 10px;
    border-bottom: 1px solid var(--border-1);
  }
  .pp-critique-scores {
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
  }
  .pp-rev-score { display: flex; flex-direction: column; gap: 6px; }
  .pp-rev-score-pips { display: flex; gap: 3px; }
  .pp-rev-pip {
    flex: 1; height: 6px; border-radius: 2px;
    background: rgba(255,255,255,0.06);
  }
  .pp-rev-pip.is-on {
    background: linear-gradient(90deg, var(--acc-indigo), var(--acc-violet));
    box-shadow: 0 0 6px rgba(136,150,255,0.40);
  }

  .pp-critique-grid {
    display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  }
  .pp-critique-col {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    overflow: hidden;
    display: flex; flex-direction: column;
  }
  .pp-critique-col-head {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid var(--border-1);
    background: rgba(255,255,255,0.015);
  }
  .pp-critique-col-icon {
    width: 26px; height: 26px;
    display: flex; align-items: center; justify-content: center;
    border: 1px solid;
    border-radius: 6px;
    flex-shrink: 0;
  }
  .pp-critique-col-body {
    display: flex; flex-direction: column;
  }
  .pp-critique-item {
    display: flex; gap: 12px;
    padding: 12px 14px;
    border-top: 1px solid var(--border-0);
  }
  .pp-critique-item:first-child { border-top: 0; }
  .pp-critique-item-num {
    font-size: 11px; width: 20px; flex-shrink: 0;
    padding-top: 2px;
  }

  /* ============ NOVELTY ============ */
  .pp-novelty { display: flex; flex-direction: column; gap: 14px; }
  .pp-novelty-headline-card {
    margin-top: 4px;
    padding: 14px 16px;
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    display: flex; flex-direction: column; gap: 12px;
  }
  .pp-novelty-headline {
    display: flex; gap: 10px; align-items: flex-start;
  }
  .pp-novelty-comp {
    display: flex; flex-direction: column; gap: 8px;
    padding-top: 10px;
    border-top: 1px solid var(--border-0);
  }
  .pp-novelty-comp-bar {
    display: flex; height: 10px; border-radius: 999px; overflow: hidden;
    background: rgba(255,255,255,0.04);
  }
  .pp-novelty-comp-seg { height: 100%; }
  .pp-novelty-comp-legend {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px;
  }
  .pp-novelty-comp-leg {
    display: flex; align-items: center; gap: 6px;
  }
  .pp-novelty-comp-swatch {
    width: 8px; height: 8px; border-radius: 2px; flex-shrink: 0;
  }

  .pp-novelty-block { display: flex; flex-direction: column; gap: 10px; }
  .pp-novelty-block-head {
    display: flex; justify-content: space-between; align-items: center; gap: 12px;
  }
  .pp-novelty-step {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 10px;
    background: rgba(136,150,255,0.10);
    color: var(--acc-indigo);
    border: 1px solid rgba(136,150,255,0.30);
    border-radius: 999px;
    font-size: 11px;
    font-family: var(--font-mono);
    letter-spacing: 0.02em;
  }

  .pp-novelty-cards {
    display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  }
  .pp-novelty-vs-card,
  .pp-novelty-conc-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    padding: 12px 14px;
  }
  .pp-novelty-vs-head {
    display: flex; justify-content: space-between; align-items: center;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border-1);
  }
  .pp-novelty-vs-body {
    display: flex; flex-direction: column;
  }
  .pp-novelty-row-line {
    display: flex; gap: 10px;
    padding: 10px 0;
    border-top: 1px solid var(--border-0);
  }
  .pp-novelty-row-line:first-child { border-top: 0; }
  .pp-novelty-line-tag {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-3);
    width: 36px; flex-shrink: 0;
    padding-top: 2px;
  }
  .pp-novelty-line-tag.is-diff { color: var(--acc-indigo); }

  .pp-novelty-conc-bullet {
    width: 8px; height: 8px; border-radius: 999px; flex-shrink: 0; margin-top: 6px;
  }
  .pp-novelty-delta {
    display: flex; gap: 8px; align-items: flex-start;
    margin-top: 10px;
    padding: 8px 10px;
    background: rgba(106,215,240,0.07);
    border-left: 2px solid var(--acc-cyan);
    border-radius: 0 5px 5px 0;
  }

  .pp-novelty-row {
    display: grid; grid-template-columns: 1fr 1fr; gap: 12px;
  }
  .pp-novelty-genuine, .pp-novelty-gaps {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 12px;
    padding: 14px 16px;
    display: flex; flex-direction: column;
  }
  .pp-novelty-genuine-list {
    display: flex; flex-direction: column; gap: 10px;
  }
  .pp-genuine-row {
    display: flex; align-items: center; gap: 10px;
  }
  .pp-genuine-bar {
    width: 90px;
    height: 5px;
    background: rgba(255,255,255,0.05);
    border-radius: 999px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .pp-genuine-bar-fill {
    height: 100%;
    box-shadow: 0 0 8px rgba(136,150,255,0.35);
  }
  .pp-novelty-gap-row {
    display: flex; gap: 10px; align-items: flex-start;
    padding: 8px 0;
    border-bottom: 1px solid var(--border-0);
  }
  .pp-novelty-gap-row:last-child { border-bottom: 0; }
  .pp-novelty-gap-num {
    font-size: 10.5px;
    color: var(--acc-violet);
    width: 22px; flex-shrink: 0;
    padding-top: 2px;
  }

  /* ============ PAPER Q&A ============ */
  .pp-paperqa { display: flex; flex-direction: column; gap: 14px; }
  .pp-paperqa-cats {
    display: flex; flex-wrap: wrap; gap: 6px;
    margin-top: 4px;
  }
  .pp-paperqa-cat {
    display: inline-flex; align-items: center; gap: 6px;
    height: 26px; padding: 0 10px;
    border-radius: 6px;
    border: 1px solid var(--border-1);
    background: var(--bg-2);
    color: var(--fg-2);
    font-size: 11.5px;
    cursor: pointer;
    transition: all .12s;
    white-space: nowrap;
  }
  .pp-paperqa-cat:hover { color: var(--fg-0); border-color: var(--border-2); }
  .pp-paperqa-cat.is-on {
    color: var(--acc-indigo);
    background: rgba(136,150,255,0.10);
    border-color: rgba(136,150,255,0.35);
  }
  .pp-paperqa-cat-n {
    font-size: 10px; color: var(--fg-3);
    padding: 0 5px; border-radius: 999px;
    background: rgba(255,255,255,0.04);
  }
  .pp-paperqa-cat.is-on .pp-paperqa-cat-n { color: var(--acc-indigo); background: rgba(136,150,255,0.16); }

  .pp-paperqa-list {
    display: flex; flex-direction: column; gap: 8px;
  }
  .pp-paperqa-empty {
    padding: 24px;
    text-align: center;
    color: var(--fg-3);
    font-size: 12px;
    border: 1px dashed var(--border-1);
    border-radius: 10px;
  }
  .pp-qa-row {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    transition: border-color .12s;
  }
  .pp-qa-row:hover { border-color: var(--border-2); }
  .pp-qa-row.is-open { border-color: rgba(136,150,255,0.30); }
  .pp-qa-q {
    position: relative;
    width: 100%;
    padding: 12px 40px 12px 14px;
    background: transparent;
    border: 0;
    text-align: left;
    cursor: pointer;
    font: inherit; color: inherit;
    display: block;
  }
  .pp-qa-chev {
    position: absolute; top: 14px; right: 14px;
    color: var(--fg-3);
    transition: transform .15s;
  }
  .pp-qa-chev.is-open { transform: rotate(180deg); color: var(--acc-indigo); }

  .pp-qa-a {
    padding: 0 14px 14px;
    border-top: 1px solid var(--border-1);
  }
  .pp-qa-a-body {
    padding-top: 14px;
    font-size: 13px;
    color: var(--fg-1);
    line-height: 1.65;
  }
  .pp-qa-follow {
    margin-top: 14px;
    padding-top: 12px;
    border-top: 1px solid var(--border-0);
    display: flex; flex-direction: column; gap: 6px;
  }
  .pp-qa-follow-q {
    display: flex; align-items: center; gap: 6px;
    padding: 7px 10px;
    border-radius: 5px;
    background: rgba(136,150,255,0.04);
    color: var(--fg-1);
    font-size: 11.5px;
    text-align: left;
    border: 1px solid transparent;
    cursor: pointer;
  }
  .pp-qa-follow-q:hover { border-color: rgba(136,150,255,0.25); color: var(--fg-0); }
  .pp-qa-a-actions {
    display: flex; gap: 6px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid var(--border-0);
  }

  /* narrower viewports: stack the 2-col grids */
  @media (max-width: 1380px) {
    .pp-critique-grid,
    .pp-novelty-cards,
    .pp-novelty-row { grid-template-columns: 1fr; }
    .pp-critique-scores { grid-template-columns: repeat(2, 1fr); }
  }
  `;
  document.head.appendChild(s);
}

export {
  CritiqueSection, NoveltySection, PaperQASection, QARow,
  CRITIQUE_DATA, NOVELTY_DATA,
};
